import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { PersonalLiveInsightItemV1 } from '../types/liveInsights';

const PRIORITY_CLASS: Record<string, string> = {
  high: 'zi-priority-high',
  medium: 'zi-priority-medium',
  low: 'zi-priority-low',
};

const UNDO_TIMEOUT_MS = 4000;

interface Props {
  items: PersonalLiveInsightItemV1[];
  onAction: (itemId: string, action: string) => void;
}

export default function PersonalQueueList({ items, onAction }: Props) {
  // Track items with pending undo (action delayed until timeout)
  const [pendingUndo, setPendingUndo] = useState<Record<string, { action: string; timerId: ReturnType<typeof setTimeout> }>>({});
  const pendingUndoRef = useRef(pendingUndo);
  pendingUndoRef.current = pendingUndo;

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(pendingUndoRef.current).forEach((p) => clearTimeout(p.timerId));
    };
  }, []);

  const handleAction = useCallback((itemId: string, action: string) => {
    // Start undo timer — action fires after UNDO_TIMEOUT_MS unless cancelled
    const timerId = setTimeout(() => {
      onAction(itemId, action);
      setPendingUndo((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }, UNDO_TIMEOUT_MS);
    setPendingUndo((prev) => ({ ...prev, [itemId]: { action, timerId } }));
  }, [onAction]);

  const handleUndo = useCallback((itemId: string) => {
    setPendingUndo((prev) => {
      const entry = prev[itemId];
      if (entry) clearTimeout(entry.timerId);
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  const openItems = items.filter((i) => i.actionState.status === 'open');
  if (openItems.length === 0 && Object.keys(pendingUndo).length === 0) return null;

  return (
    <div className="zi-personal-queue">
      {openItems.map((item) => {
        const pending = pendingUndo[item.itemId];
        if (pending) {
          return (
            <div key={item.itemId} className="zi-queue-item zi-queue-item-pending">
              <span className="zi-queue-msg zi-queue-msg-dimmed">
                {pending.action === 'completed' ? 'Marked done' : 'Dismissed'}
              </span>
              <button
                className="zi-queue-btn zi-queue-btn-undo"
                onClick={() => handleUndo(item.itemId)}
                aria-label={`Undo ${pending.action}: ${item.message}`}
              >
                Undo
              </button>
            </div>
          );
        }
        return (
          <div key={item.itemId} className="zi-queue-item">
            <span className={`zi-priority-dot ${PRIORITY_CLASS[item.priority] ?? ''}`} />
            <span className="zi-queue-msg">{item.message}</span>
            <div className="zi-queue-actions">
              <button
                className="zi-queue-btn"
                onClick={() => handleAction(item.itemId, 'completed')}
                title="Mark as done"
                aria-label={`Mark done: ${item.message}`}
              >
                ✓
              </button>
              <button
                className="zi-queue-btn zi-queue-btn-dismiss"
                onClick={() => handleAction(item.itemId, 'dismissed')}
                title="Dismiss"
                aria-label={`Dismiss: ${item.message}`}
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
